/**
 * MedicalPersonnelSection.jsx - Tab 5 personnel section: losses of the medical personnel (R1-08, 2026-10-25, Dr. Tatar).
 *
 * The calculation follows the medical personnel of each brigade day by day (they die at 1.15 times the rate of the unit they support, more
 * with deliberate targeting). The share still alive takes the same share off the prolonged field care benefit (pfcFromFacilities.js
 * MEDIC_LOSS_REDUCES_PFC_ON). This table shows what was lost and what is left, brigade by brigade, with the benefit kept on the last day.
 *
 * Input: projections.by_brigade[*].medical_personnel (initial_count, total_casualties, remaining, final_capacity_percent, assessment,
 * initial_count_source) and by_day[*].resources.evac_wait.medic_share.
 */
import React from 'react';
import PropTypes from 'prop-types';
import { useTranslation } from 'react-i18next';
import { Alert, Box, Card, CardContent, Chip, Table, TableBody, TableCell, TableContainer, TableHead, TableRow, Typography } from '@mui/material';

const STATUS_COLOR = { ADEQUATE: 'success', DEGRADED: 'warning', CRITICAL: 'error' };

const keptOnLastDay = (brigade) => {
  const days = Array.isArray(brigade?.by_day) ? brigade.by_day : [];
  for (let i = days.length - 1; i >= 0; i--) {
    const share = days[i]?.resources?.evac_wait?.medic_share;
    if (Number.isFinite(share)) return Math.round(share * 100);
  }
  return null;
};

const MedicalPersonnelSection = ({ brigades }) => {
  const { t } = useTranslation();
  const rows = (Array.isArray(brigades) ? brigades : []).filter((b) => b && b.medical_personnel && Number(b.medical_personnel.initial_count) > 0);
  if (rows.length === 0) return null;

  const total = rows.reduce((a, b) => ({
    start: a.start + Number(b.medical_personnel.initial_count),
    lost: a.lost + Number(b.medical_personnel.total_casualties || 0),
    left: a.left + Number(b.medical_personnel.remaining || 0)
  }), { start: 0, lost: 0, left: 0 });
  const totalPercent = total.start > 0 ? Math.round((total.left / total.start) * 100) : 0;
  const assumed = rows.some((b) => b.medical_personnel.initial_count_source === 'ASSUMED_3_PERCENT_OF_STRENGTH');

  return (
    <Card variant="outlined" sx={{ mt: 3 }} data-testid="medical-personnel-section">
      <CardContent>
        <Typography variant="h6" gutterBottom>{t('tab5.medpers_title', 'Medical personnel losses')}</Typography>
        <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
          {t('tab5.medpers_intro', 'Medics and other medical staff of each brigade are lost at 1.15 times the rate of the unit they support. The share still alive takes the same share off the prolonged field care benefit (the part of the evacuation wait that the Role 1 medic carries).')}
        </Typography>
        <TableContainer>
          <Table size="small">
            <TableHead>
              <TableRow>
                <TableCell>{t('tab5.medpers_brigade', 'Brigade')}</TableCell>
                <TableCell align="right">{t('tab5.medpers_start', 'At start')}</TableCell>
                <TableCell align="right">{t('tab5.medpers_lost', 'Lost')}</TableCell>
                <TableCell align="right">{t('tab5.medpers_left', 'Left')}</TableCell>
                <TableCell align="right">{t('tab5.medpers_capacity', 'Capacity left')}</TableCell>
                <TableCell align="right">{t('tab5.medpers_pfc_kept', 'Field care benefit kept (last day)')}</TableCell>
                <TableCell>{t('tab5.medpers_status', 'Status')}</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {rows.map((b, i) => {
                const mp = b.medical_personnel;
                const kept = keptOnLastDay(b);
                return (
                  <TableRow key={b.brigade_id || i}>
                    <TableCell>{b.brigade_name || b.brigade_id || `#${i + 1}`}</TableCell>
                    <TableCell align="right">{Math.round(mp.initial_count)}</TableCell>
                    <TableCell align="right">{Math.round(mp.total_casualties || 0)}</TableCell>
                    <TableCell align="right">{Math.round(mp.remaining || 0)}</TableCell>
                    <TableCell align="right">{Number.isFinite(mp.final_capacity_percent) ? `${mp.final_capacity_percent}%` : '-'}</TableCell>
                    <TableCell align="right">{kept === null ? '-' : `${kept}%`}</TableCell>
                    <TableCell>
                      <Chip size="small" color={STATUS_COLOR[mp.assessment] || 'default'} label={t(`tab5.medpers_status_${String(mp.assessment || 'UNKNOWN').toLowerCase()}`, mp.assessment || 'UNKNOWN')} />
                    </TableCell>
                  </TableRow>
                );
              })}
              {rows.length > 1 && (
                <TableRow>
                  <TableCell><strong>{t('tab5.medpers_total', 'Total')}</strong></TableCell>
                  <TableCell align="right"><strong>{Math.round(total.start)}</strong></TableCell>
                  <TableCell align="right"><strong>{Math.round(total.lost)}</strong></TableCell>
                  <TableCell align="right"><strong>{Math.round(total.left)}</strong></TableCell>
                  <TableCell align="right"><strong>{totalPercent}%</strong></TableCell>
                  <TableCell />
                  <TableCell />
                </TableRow>
              )}
            </TableBody>
          </Table>
        </TableContainer>
        {assumed && (
          <Box sx={{ mt: 2 }}>
            <Alert severity="info" variant="outlined">
              {t('tab5.medpers_assumed', 'The medical personnel count of at least one brigade is not entered: 3 percent of its strength is assumed.')}
            </Alert>
          </Box>
        )}
      </CardContent>
    </Card>
  );
};

MedicalPersonnelSection.propTypes = {
  brigades: PropTypes.array
};

export default MedicalPersonnelSection;
